/**
 * 兼容层：旧组件仍可 useAuth()，底层走 Zustand authStore + Repository。
 * 新代码请直接使用 useAuthStore。
 */
import React, { createContext, useContext, useEffect } from 'react'
import { useAuthStore } from '../store/authStore'
import type { AuthUser } from '../types/auth'

type AuthContextType = {
  user: AuthUser | null
  loading: boolean
  isAuthenticated: boolean
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  signUpWithEmail: (
    email: string,
    password: string,
    confirmPassword?: string
  ) => Promise<{ error: Error | null }>
  /** 首版不做 OAuth / 邮箱重置，保留签名避免调用方崩溃 */
  signInWithGoogle: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useAuthStore()

  useEffect(() => {
    void store.hydrate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: AuthContextType = {
    user: store.user,
    loading: store.isLoading,
    isAuthenticated: store.isAuthenticated,

    signInWithEmail: async (email, password) => {
      try {
        await store.login(email, password)
        return { error: null }
      } catch (e) {
        return { error: e instanceof Error ? e : new Error(String(e)) }
      }
    },

    signUpWithEmail: async (email, password, confirmPassword) => {
      try {
        await store.register(email, password, confirmPassword ?? password)
        return { error: null }
      } catch (e) {
        return { error: e instanceof Error ? e : new Error(String(e)) }
      }
    },

    signInWithGoogle: async () => {
      throw new Error('Google 登录已下线，请使用邮箱注册/登录')
    },

    resetPassword: async () => ({
      error: new Error('密码重置暂未开放，请联系支持或重新注册'),
    }),

    signOut: () => store.logout(),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
